import { Alert, Checkbox, Descriptions, Input, InputNumber, Modal, Spin, Table, Tag, Typography } from "antd";
import React, { useEffect, useState } from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import {
  ReturnRow,
  VendorReturnContext,
  VendorReturnContextItem,
  getVendorReturnContext,
  raiseVendorReturn,
} from "../returns.api";

interface Props {
  row: ReturnRow | null;
  onClose: () => void;
  onRaised: () => void;
}

type Line = { selected: boolean; qty: number; rate: number };

const MODE_LABEL = {
  ship: "We send it — a truck is booked from our warehouse to the vendor",
  collect: "Vendor collects — their vehicle picks it up from our warehouse",
};

const VendorReturnModal: React.FC<Props> = ({ row, onClose, onRaised }) => {
  const [ctx, setCtx] = useState<VendorReturnContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState<Record<number, Line>>({});
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!row) return;
    setCtx(null);
    setNote("");
    setLoading(true);
    getVendorReturnContext(row.stage_id).then((data) => {
      setLoading(false);
      if (!data) return;
      setCtx(data);
      setLines(Object.fromEntries(data.items.map((i) => [i.allocation_item_id, {
        selected: Number(i.default_qty) > 0,
        qty: Number(i.default_qty),
        rate: Number(i.po_rate),
      }])));
    });
  }, [row]);

  const update = (id: number, patch: Partial<Line>) =>
    setLines((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const picked = Object.entries(lines).filter(([, l]) => l.selected && l.qty > 0);
  const total = picked.reduce((sum, [, l]) => sum + l.qty * l.rate, 0);
  const invalid = !picked.length || picked.some(([, l]) => !(l.rate > 0));

  const handleOk = async () => {
    if (!row) return;
    setSaving(true);
    const res = await raiseVendorReturn(row.stage_id, {
      items: picked.map(([id, l]) => ({ allocation_item_id: Number(id), qty: l.qty, rate: l.rate })),
      note: note.trim() || undefined,
    });
    setSaving(false);
    if (res) {
      onRaised();
      onClose();
    }
  };

  const columns = [
    {
      title: "",
      key: "pick",
      width: 40,
      render: (_: unknown, i: VendorReturnContextItem) => (
        <Checkbox checked={lines[i.allocation_item_id]?.selected}
                  onChange={(e) => update(i.allocation_item_id, { selected: e.target.checked })} />
      ),
    },
    {
      title: "Item",
      key: "item",
      render: (_: unknown, i: VendorReturnContextItem) => (
        <div>
          <div>{i.product_name}</div>
          {i.sku && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{i.sku}</Typography.Text>}
        </div>
      ),
    },
    {
      title: "Received at QC",
      key: "received",
      width: 110,
      align: "right" as const,
      render: (_: unknown, i: VendorReturnContextItem) => Number(i.received_qty),
    },
    {
      title: "Qty to send",
      key: "qty",
      width: 120,
      render: (_: unknown, i: VendorReturnContextItem) => (
        <InputNumber size="small" min={0} max={Number(i.received_qty)} style={{ width: "100%" }}
                     disabled={!lines[i.allocation_item_id]?.selected}
                     value={lines[i.allocation_item_id]?.qty}
                     onChange={(v) => update(i.allocation_item_id, { qty: Number(v ?? 0) })} />
      ),
    },
    {
      title: "Rate (ex-tax)",
      key: "rate",
      width: 130,
      render: (_: unknown, i: VendorReturnContextItem) => (
        <InputNumber size="small" min={0} style={{ width: "100%" }} prefix="₹"
                     disabled={!lines[i.allocation_item_id]?.selected}
                     value={lines[i.allocation_item_id]?.rate}
                     onChange={(v) => update(i.allocation_item_id, { rate: Number(v ?? 0) })} />
      ),
    },
  ];

  return (
    <Modal
      title={`Return to vendor — return #R-${row?.stage_id ?? ""}`}
      open={row !== null}
      onCancel={onClose}
      onOk={handleOk}
      okText="Return to vendor"
      okButtonProps={{ disabled: !ctx?.eligible || invalid }}
      confirmLoading={saving}
      width={760}
      destroyOnClose
      centered
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        {loading || !ctx ? (
          <div style={{ textAlign: "center", padding: 24 }}><Spin /></div>
        ) : (
          <>
            {!ctx.eligible && (
              <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={ctx.blocked_reason} />
            )}
            <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Vendor">{ctx.supplier?.name ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="PO">{ctx.po_number ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Vendor warehouse">{ctx.supplier_warehouse ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="How">
                {ctx.mode ? <Tag color={ctx.mode === "ship" ? "blue" : "purple"}>{MODE_LABEL[ctx.mode]}</Tag> : "—"}
              </Descriptions.Item>
            </Descriptions>
            {ctx.eligible && (
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                     message="A vendor credit is filed in Zoho at these rates once the vendor has billed the PO. The goods leave only after that." />
            )}
            <Table<VendorReturnContextItem>
              rowKey="allocation_item_id"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={ctx.items}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}><strong>Vendor credit (ex-tax)</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}><strong>₹{total.toFixed(2)}</strong></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
            <Input.TextArea rows={2} maxLength={500} style={{ marginTop: 12 }} placeholder="Note (optional)"
                            value={note} onChange={(e) => setNote(e.target.value)} />
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default VendorReturnModal;
